import Vue from 'vue';
import Vuex from 'vuex';
import isElectron from 'is-electron';
import fs from 'fs';
import env from '@/assets/env';

// eslint-disable-next-line no-unused-vars
import { combine, clone, RemoveEndSlashes } from '@/lib/utils';

// eslint-disable-next-line import/no-cycle
import Forms from '@/lib/Forms';
import {
	OnAuthStateChanged,
	SignOut,
} from '@/lib/db/auth';

import { GetFirestoreUser } from '@/lib/db/users';

let shell;

if ( isElectron() ) {
	shell = require( 'electron' ).shell; // eslint-disable-line
}

Vue.use( Vuex );

const store = new Vuex.Store( {

	state : {

		packageVersion : env.PACKAGE_VERSION || '0',

		token : 'some token',

		displayStates : {},

		forms : [],

		user : {
			name  : null,
			email : null,
			uid   : null,
		},

		loginState : 'unattempted',

		navState : '',

		isElectron : isElectron(),

		driveLinkState : 'unattempted',

		// need to add something that checks the local storage if its null then set the state to this
		postgresUser : {
			name                 : '',
			email                : '',
			permission_level     : '',
			notifications_status : false,
			created_by           : 0,
			status               : 'Enabled',
			driveLink            : null,
		}
	},

	// guess some people dont believe in getters but i do
	getters : {
		postgresUser : state => state.postgresUser,
		navState     : state => state.navState,
		appVersion   : state => state.packageVersion,
	},


	mutations : {

		/* eslint-disable no-param-reassign */
		updateUser( state, user ) {
			state.user = clone( user );
		},

		updatePostgresUser( state, user ) {
			state.postgresUser = user;
			state.driveLinkState = 'valid';
		},

		updatePostgresUserGDrive( state, GDriveLink ) {
			state.postgresUser.driveLink = GDriveLink;
		},

		clearUser( state ) {
			state.user = {
				name  : null,
				email : null,
				uid   : null,
			};
		},

		changeLoginState( state, passed ) {

			if ( !passed ) {
				state.loginState = 'failed';
			}

			else if ( passed ) {
				state.loginState = 'succeeded';
			}

		},

		form( state, form ) {
			state.forms.push( form );
		},

		removeForm( state ) {
			state.forms.pop();
		},

		toggleEmailNotifications( state ) {
			state.user.userSettings.emailNotifications = !state.user.userSettings.emailNotifications;
		},

		// below is the data for the display state
		displayState( state, options ) {
			const { index } = options;

			Vue.set( state.displayStates, index, options ); // eslint-disable-line
		},

		updateDisplayState( state, options ) {
			const { index } = options;
			const keys      = Object.keys( options ).filter( a => a !== 'index' );

			keys.forEach( ( key ) => {
				Vue.set( state.displayStates[index], key, options[key] ); // eslint-disable-line
			} );
		},

		removeDisplayState( state, index ) {
			Vue.delete( state.displayStates, index ); // eslint-disable-line
		},

		updateStore( state, [prop, value] ) {
			state[prop] = value;
		},

		setNavState( state, navState ) {
			state.navState = navState;
		},

		validateDriveLink( state, driveLinkState ) {
			state.driveLinkState = driveLinkState;
		}

	},

	actions : {

		initAuthListener : ( { commit, dispatch } ) => new Promise( ( resolve ) => {

			OnAuthStateChanged( ( user ) => {

				if ( !user ) {
					commit( 'clearUser' );
					commit( 'changeLoginState', false );
					resolve( user );
					return;
				}

				user.getIdToken( true )
					.then( ( token ) => {
						commit( 'updateStore', ['token', token] );
					} );

				const { uid } = user;

				GetFirestoreUser( uid, ( firestoreUser ) => {
					const newUser = {
						...firestoreUser,
						uid,
					};

					commit( 'updateUser', newUser );
					commit( 'changeLoginState', true );

					dispatch( 'validateDriveLink', newUser.driveLink );

					resolve( newUser );
				} );

			} );
		} ),

		loadWithDisplayState : async ( { dispatch }, { options, method } ) => {
			const displayState = await dispatch( 'displayState', options );

			return method()
				.then( ( res ) => {
					displayState.resolve( options.successMessage || 'Succeeded' );

					return res;
				} )
				.catch( () => {
					displayState.fail( { message : options.failMessage || 'Failed' } );

					throw new Error( options.failMessage || 'Failed to complete method' );
				} );
		},

		displayState( { state, commit }, options ) {

			const displayedStates = Object.keys( state.displayStates );
			const max             = displayedStates.length === 0 ? 0 : Math.max( ...displayedStates );

			const index = max.index ? max.index + 1 : max + 1;

			commit( 'displayState', Object.assign( { index }, options ) );

			return {
				resolve( message ) {
					commit( 'updateDisplayState', {
						state : 'succeeded',
						message,
						index,
					} );

					window.setTimeout( () => {
						commit( 'removeDisplayState', index );
					}, 2000 );
				},

				fail( { header, message } ) {
					commit( 'updateDisplayState', {
						state : 'failed',
						index,
						message,
						header,
					} );
				},

				index,
			};
		},

		removeDisplayState( { commit }, index ) {
			commit( 'removeDisplayState', index );
		},

		form( { commit }, form ) {
			// first, create our form from the type
			const { type, startingData } = form;
			const args                   = form.args || [];

			const createdForm = Forms[type]( ...args );

			// if possible, try to merge the startingData of
			// our form with the input starting data. Ideally,
			// we should be able to specificy only certain properties
			// of the data and the form will have it
			const formDefaultData = createdForm.startingData();
			if ( startingData ) {
				createdForm.startingData = combine( formDefaultData, startingData );
			}

			else {
				createdForm.startingData = formDefaultData;
			}

			const i = !store.forms ? 0 : store.forms.length;
			const close = () => commit( 'removeForm', { i, resolved : true } );

			// create a promise which will be returned to the
			// caller of this function, and which will be resolved
			// by the form component when the submit button is
			// pressed and the validation is complete
			const promise = new Promise( ( resolve, reject ) => {
				createdForm.resolve = value => resolve( {
					close,
					value
				} );
				createdForm.resolve.close = close;
				createdForm.reject  = ( message ) => {
					commit( 'removeForm' );
					reject( message );
				};
			} );

			// commit this form
			commit( 'form', createdForm );

			return promise;
		},

		login( { commit }, user ) {
			const userName = user.name.split( ' ' );

			const newUser = {
				uid          : user.uid,
				usersKey     : user.usersKey,
				name         : userName,
				email        : user.email,
				userSettings : null,
			};

			commit( 'updateUser', newUser );
			commit( 'changeLoginState', true );
		},

		logout( { commit } ) {
			SignOut();
			commit( 'clearUser' );
			commit( 'changeLoginState', false );
		},

		toggleEmailNotifications( { commit } ) {
			commit( 'toggleEmailNotifications' );
		},

		setNavState( { commit }, state ) {
			commit( 'setNavState', state );
		},

		openDocument( { dispatch }, link ) {

			if ( !link ) {
				const failedOptions = {
					message : 'No document link was found for this file.',
					state   : 'failed',
				};

				this.dispatch( 'displayState', failedOptions );
				return;
			}

			// check to see if it's an electron app, if it's not, tell
			// the user they need to be on the desktop version. If they are,
			// continue with the ux.
			if ( !isElectron() ) {
				this.dispatch( 'copyLinkToClipboard', link );
				return;
			}

			const displayStateOptions = {
				message        : 'Opening Document',
				successMessage : 'Opened Document',
				failMessage    : 'Could not open document',
				state          : 'loading',
			};

			dispatch( 'loadWithDisplayState', {
				options : displayStateOptions,
				method  : () => new Promise( ( resolve, reject ) => {

					link = link.replace( /\\/g, '/' );

					const formattedLink = ( () => {

						let userDriveLink = store.state.postgresUser.driveLink;

						// console.log( `driveLink : ${userDriveLink}` );

						while ( userDriveLink.endsWith( '/' ) || userDriveLink.endsWith( '\\' ) ) {
							userDriveLink = userDriveLink.slice( 0, userDriveLink.length - 1 );
						}

						while ( link.startsWith( '/' ) || link.startsWith( '\\' ) ) {
							link = link.slice( 1, link.length );
						}

						return `${userDriveLink}/${link}`;

					} )();

					// console.log( `formattedLink : ${formattedLink}` );

					const openedItem = shell.openExternal( `file:///${formattedLink}` );

					openedItem
						.then( () => {
							resolve();
						} )
						.catch( () => {
							reject();
						} );
				} ),
			} );
		},

		copyLinkToClipboard( { dispatch }, documentLink ) {

			documentLink = documentLink.replace( /\\/g, '/' );


			const formattedLink = ( () => {

				let userDriveLink = store.state.user.driveLink;

				if ( userDriveLink.endsWith( '/' ) ) {
					userDriveLink = userDriveLink.slice( 0, userDriveLink.length - 1 );
				}

				if ( documentLink.startsWith( '/' ) ) {
					documentLink = documentLink.slice( 1, documentLink.length );
				}

				return `${userDriveLink}/${documentLink}`;

			} )();

			// write the copy logic to the users computer
			const options = {
				message     : 'Document URL copied to clipboard. Please switch to the desktop version to open the file',
				failMessage : 'Could not copy document URL',
			};

			// javascript needs an input element in order to copy
			// a value to clipboard
			const input = document.createElement( 'input' );

			document.body.append( input );

			input.style.opacity = 0;
			input.value         = formattedLink;

			input.select();
			input.setSelectionRange( 0, 999999 ); // no clue what this does but w3 schools says it's necessary for mobile

			document.execCommand( 'copy' );

			dispatch( 'displayState', options )
				.then( ( stateIndicator ) => {

					setTimeout( () => {
						this.dispatch( 'removeDisplayState', stateIndicator.index );
					}, 2500 );

				} );

			document.body.removeChild( input );
		},

		validateDriveLink( { state, commit } ) {
			// console.log( state.postgresUser.driveLink );


			if ( !isElectron() ) {
				// console.log( '[STORE] not-electron. cannot validate' );
				commit( 'validateDriveLink', 'not-electron' );
				return;
			}

			if ( !state.postgresUser.driveLink ) {
				// console.log( `[STORE] drive link invalid--empty: ${state.postgresUser.driveLink}` );
				commit( 'validateDriveLink', 'invalid' );
				return;
			}

			const regexForEndSlashes = /([\\/]+)$/;

			// console.log( fs.access( state.postgresUser.driveLink ) );

			if ( !fs.existsSync( state.postgresUser.driveLink ) ) {
				// console.log( `[STORE] drive link invalid--fs not exists: ${state.postgresUser.driveLink}` );
				commit( 'validateDriveLink', 'invalid' );
				return;
			}

			if ( state.postgresUser.driveLink.match( regexForEndSlashes ) ) {
				// console.log( `[STORE] drive link invalid--ends with slashes: ${state.postgresUser.driveLink}` );
				commit( 'validateDriveLink', 'invalid' );
				return;
			}

			commit( 'validateDriveLink', 'valid' );
		}
	},

} );

export default store;
